import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    Box,
    Alert,
    Button,
    Divider,
    IconButton,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { CardRegisterButton } from '../Payment/CardRegisterButton';
import { useVendorStore } from '../../store/vendorStore';
import { useLoginFormDialogStore } from '../../store/loginFormDialogStore';
import { useFullScreenDialogStore } from '../../store/fullScreenDialogStore';
import { useNavigate } from 'react-router-dom';
import { post, get } from '../../helpers/api.helper';
import { getFittingProducts } from '../../utils/subscriptionApi';
import { getUserCards } from '../../services/billing.service';
import config from '../../config/config';

interface SubscriptionCheckoutDialogProps {
    planId?: string;
    planTitle?: string;
    planPrice?: number | string;
    planPeriod?: string;
}

export function SubscriptionCheckoutDialog() {
    const { vendorId } = useVendorStore();
    const { setIsOpen: openLoginDialog } = useLoginFormDialogStore((state) => state.action);
    const { isOpen, planData, action } = useFullScreenDialogStore();
    const { close } = action;
    const { planId = '', planTitle = '', planPrice = 0, planPeriod = '' } = planData || {};
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [activeStep, setActiveStep] = useState(0);
    const [cardRegistered, setCardRegistered] = useState(false);
    const [subscriptionComplete, setSubscriptionComplete] = useState(false);
    const [billingData, setBillingData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fittingSubscriptionPlanId, setFittingSubscriptionPlanId] = useState<string>('');
    const [hasExistingCard, setHasExistingCard] = useState<boolean | null>(null);
    const [existingCardBid, setExistingCardBid] = useState<string | null>(null);
    const [businessCode, setBusinessCode] = useState<string>('');
    const [isCodeVerified, setIsCodeVerified] = useState<boolean>(false);
    const [verifying, setVerifying] = useState<boolean>(false);

    // 다이얼로그가 열릴 때마다 상태 초기화
    useEffect(() => {
        if (isOpen) {
            setActiveStep(0);
            setCardRegistered(false);
            setSubscriptionComplete(false);
            setBillingData(null);
            setLoading(false);
            setError(null);
            setHasExistingCard(null);
            setExistingCardBid(null);
            setBusinessCode('');
            setIsCodeVerified(false);
            setVerifying(false);
        }
    }, [isOpen]);

    // 로그인 체크
    useEffect(() => {
        if (isOpen && !vendorId) {
            close();
            openLoginDialog(true);
        }
    }, [isOpen, vendorId, openLoginDialog, close]);

    // fittingSubscriptionPlanId 가져오기 및 기존 카드 확인
    useEffect(() => {
        const fetchData = async () => {
            if (isOpen && planId && vendorId) {
                try {
                    // fittingSubscriptionPlanId 가져오기
                    const response = await getFittingProducts();
                    const fittingProduct = response.data.find((item) => item.id === planId);
                    if (fittingProduct && fittingProduct.fittingSubscriptionPlans.length > 0) {
                        setFittingSubscriptionPlanId(fittingProduct.fittingSubscriptionPlans[0].id);
                    }

                    // 기존 카드 확인
                    const cardResponse = await getUserCards(vendorId);

                    const availableCards = cardResponse.itemList.filter(
                        (card) => (card.isUsed === 'Y' || card.isUsed === 't') && !card.deletedAt,
                    );

                    if (availableCards.length > 0) {
                        setHasExistingCard(true);
                        setExistingCardBid(availableCards[0].bid);
                    } else {
                        setHasExistingCard(false);
                    }
                } catch (error) {
                    console.error('Failed to fetch data:', error);
                    setHasExistingCard(false);
                }
            }
        };

        fetchData();
    }, [isOpen, planId, vendorId]);

    // 플랜 정보가 없으면 다이얼로그 닫기
    useEffect(() => {
        if (isOpen && (!planId || !planTitle)) {
            close();
        }
    }, [isOpen, planId, planTitle, close]);

    const handleCardRegistrationSuccess = (data: any) => {
        setBillingData(data);
        setCardRegistered(true);
        setActiveStep(1);

        // 이미 approve API가 호출된 경우 중복 호출 방지
        if (data.alreadyApproved) {
            setSubscriptionComplete(true);
            return;
        }

        if (data.BID) {
            handleCreateSubscription(data);
        } else {
            console.error('BID가 없어서 구독을 생성할 수 없습니다.');
        }
    };

    const handleCardRegistrationError = (error: Error) => {
        console.error('Card registration failed:', error);
        setError('카드 등록에 실패했습니다. 다시 시도해주세요.');
    };

    const handleVerifyCode = async () => {
        if (!businessCode.trim()) {
            setError('초대코드를 입력해주세요.');
            return;
        }

        try {
            setVerifying(true);
            setError(null);

            const response = await get(
                `${config.api.matching_api}/billing/admin-user/invite-code/${businessCode.trim()}`,
            );

            if (response && response.data) {
                setIsCodeVerified(true);
                toast.success('초대코드가 인증되었습니다.', {
                    position: 'top-center',
                    autoClose: 2000,
                });
            } else {
                throw new Error('유효하지 않은 초대코드입니다.');
            }
        } catch (error) {
            console.error('Code verification failed:', error);
            setIsCodeVerified(false);
            setError(error instanceof Error ? error.message : '초대코드 인증에 실패했습니다.');
        } finally {
            setVerifying(false);
        }
    };

    const handleCreateSubscriptionWithExistingCard = async (bid: string) => {
        try {
            setLoading(true);
            setError(null);
            setActiveStep(1); // 구독 생성 중 단계로 이동

            // 초대코드 필수 입력 체크
            if (!businessCode.trim()) {
                throw new Error('초대코드를 입력해주세요.');
            }

            // 초대코드 인증 체크
            if (!isCodeVerified) {
                throw new Error('초대코드 인증을 완료해주세요.');
            }

            // fittingSubscriptionPlanId가 없으면 에러
            if (!fittingSubscriptionPlanId) {
                throw new Error('구독 플랜 정보를 찾을 수 없습니다.');
            }

            const response = await post(`${config.api.matching_api}/billing/approve`, {
                subscriptionPlanId: fittingSubscriptionPlanId,
                userId: vendorId,
                bid: bid,
                amount: Number(planPrice),
                goodsName: planTitle,
                businessCode: businessCode,
            });

            const result = response.data;
            if (result && result.ResultCode === '3001') {
                // 구독 완료 성공 토스트 메시지 표시
                toast.success('구독이 성공적으로 완료되었습니다!', {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setSubscriptionComplete(true);
            } else {
                const errorMsg = result?.ResultMsg || '구독 결제 처리에 실패했습니다.';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Subscription creation failed:', error);
            setError(error instanceof Error ? error.message : '구독 생성에 실패했습니다.');
            setActiveStep(0);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSubscription = async (cardData: any) => {
        try {
            setLoading(true);
            setError(null);

            // 초대코드 필수 입력 체크
            if (!businessCode.trim()) {
                throw new Error('초대코드를 입력해주세요.');
            }

            // 초대코드 인증 체크
            if (!isCodeVerified) {
                throw new Error('초대코드 인증을 완료해주세요.');
            }

            // fittingSubscriptionPlanId가 없으면 에러
            if (!fittingSubscriptionPlanId) {
                throw new Error('구독 플랜 정보를 찾을 수 없습니다.');
            }

            const response = await post(`${config.api.matching_api}/billing/approve`, {
                subscriptionPlanId: fittingSubscriptionPlanId,
                userId: vendorId,
                bid: cardData.BID,
                amount: Number(planPrice),
                goodsName: planTitle,
                businessCode: businessCode,
            });

            const result = response.data;
            if (result && result.ResultCode === '3001') {
                // 구독 완료 성공 토스트 메시지 표시
                toast.success('구독이 완료되었습니다.', {
                    position: 'top-center',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                setSubscriptionComplete(true);
            } else {
                const errorMsg = result?.ResultMsg || '구독 결제 처리에 실패했습니다.';
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error('Subscription creation failed:', error);
            setError(error instanceof Error ? error.message : '구독 생성에 실패했습니다.');
            setActiveStep(0);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // 구독이 완료된 상태에서 팝업을 닫으면 완료 처리
        if (subscriptionComplete) {
            handleComplete();
        } else {
            close();
        }
    };

    const handleComplete = () => {
        close();
        // 구독 완료 후 계정 설정의 청구서 탭으로 이동
        navigate('/account/settings#billing');
    };

    const renderPrice = () => {
        if (typeof planPrice === 'number') {
            // planPeriod에서 월/연 정보 추출
            const periodText = planPeriod.includes('년') ? '연' : '월';
            return `${periodText} ₩${planPrice.toLocaleString()}`;
        }
        return planPrice;
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    borderRadius: isMobile ? '0px' : '12px',
                    maxHeight: isMobile ? '100vh' : '90vh',
                },
            }}
        >
            <DialogContent
                sx={{
                    px: isMobile ? 3 : 8,
                    py: isMobile ? 2 : 3,
                    position: 'relative',
                    pt: isMobile ? 4 : 6,
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: 12,
                        zIndex: 1,
                        color: '#000',
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Typography
                    variant="h1"
                    sx={{
                        mb: 2,
                        fontWeight: 700,
                        fontSize: isMobile ? '24px' : '32px',
                        pt: isMobile ? 1 : 2,
                    }}
                >
                    지금 바로 AI 피팅을 시작하세요
                </Typography>
                {/* Plan Information */}
                <Box
                    sx={{
                        backgroundColor: '#f5f5f5',
                        borderRadius: '10px',
                        p: isMobile ? 2 : 3,
                        mb: isMobile ? 2 : 3,
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: isMobile ? '18px' : '20px' }}>
                        {planTitle} 플랜
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, fontSize: isMobile ? '18px' : '20px' }}>
                        {renderPrice()}(부가세 포함)
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ opacity: 0.9, fontSize: isMobile ? '0.75rem' : '0.675rem', fontWeight: 500 }}
                    >
                        간단한 설정으로도 실사 같은 고품질. 캐릭터 일관성 안정 유지. 조명·원근감까지 자연스럽게 보존.
                        멀티 이미지 합성 & 스타일 반영. 빠른 이미지 생성 가능.
                    </Typography>
                    <Divider sx={{ my: isMobile ? 1.5 : 2, borderColor: '#333' }} />
                    <Typography variant="body2" sx={{ opacity: 0.7, fontSize: isMobile ? '0.75rem' : '0.675rem' }}>
                        구매 후 14일 이내 취소 가능하며, 결제 주기 종료 시 취소 처리됩니다. 그 외 구독 관련 문의는
                        고객센터로 문의해주세요.
                    </Typography>
                </Box>

                {/* Business Code Section */}
                <Box sx={{ mb: isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: isMobile ? '14px' : '15px' }}>
                        초대코드 <span style={{ color: '#FF4B6B' }}>*</span>
                        {isCodeVerified && (
                            <CheckCircleIcon
                                sx={{
                                    ml: 1,
                                    color: '#10B981',
                                    fontSize: '20px',
                                    verticalAlign: 'middle',
                                }}
                            />
                        )}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                            variant="outlined"
                            placeholder="초대코드를 입력해주세요"
                            value={businessCode}
                            onChange={(e) => {
                                setBusinessCode(e.target.value);
                                setIsCodeVerified(false); // 코드 변경 시 인증 상태 초기화
                            }}
                            required
                            disabled={isCodeVerified}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    height: '40px',
                                    '& fieldset': {
                                        borderColor: isCodeVerified ? '#10B981' : '#E5E7EB',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: isCodeVerified ? '#10B981' : '#D1D5DB',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: isCodeVerified ? '#10B981' : '#FF4B6B',
                                    },
                                },
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleVerifyCode}
                            disabled={verifying || !businessCode.trim() || isCodeVerified}
                            sx={{
                                height: '40px',
                                minWidth: '80px',
                                backgroundColor: isCodeVerified ? '#10B981' : '#374151',
                                color: 'white',
                                borderRadius: '12px',
                                fontSize: '14px',
                                fontWeight: 600,
                                textTransform: 'none',
                                transition: 'all 0.2s ease-in-out',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                    backgroundColor: isCodeVerified ? '#059669' : '#1F2937',
                                    transform: 'translateY(-1px)',
                                    boxShadow: isCodeVerified
                                        ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                                        : '0 4px 12px rgba(55, 65, 81, 0.4)',
                                },
                                '&:disabled': {
                                    backgroundColor: '#9CA3AF',
                                    transform: 'none',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                },
                            }}
                        >
                            {verifying ? '인증중...' : isCodeVerified ? '인증완료' : '인증'}
                        </Button>
                    </Box>
                </Box>

                {/* Payment Method Section */}
                <Box sx={{ mb: isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: isMobile ? '14px' : '15px' }}>
                        * 현재 신용카드 결제만 가능합니다.
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ mb: 2, opacity: 0.7, fontSize: isMobile ? '0.75rem' : '0.675rem' }}
                    >
                        구독 결제를 완료하려면 '동의 및 구독 결제하기'를 클릭하여 완료하세요.
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ mb: 2, opacity: 0.7, fontSize: isMobile ? '0.75rem' : '0.675rem' }}
                    >
                        '동의 및 구독 결제하기'를 클릭하면, 구독 결제를 완료함과 동시에 멤버십을 즉시 시작하게 되고{' '}
                        {planPrice !== 'Custom' && `${renderPrice()}(부가세 포함, 변동 가능)의 구독료가`} 회원님의
                        저장된 결제 수단에 결제 주기마다 반복적으로 정기 청구됩니다. 멤버십은 계정 설정을 통하거나
                        고객센터에 문의해 언제든지 취소할 수 있으며, 구매 후 14일 이내 취소 가능하며, 결제 기간 종료 시
                        취소 처리됩니다. 즉시 취소를 비롯해 취소 및 환불에 대해 보다 자세히 알고 싶다면, 고객센터로
                        연락주시거나, 취소 및 환불 정책을 확인하세요. 멤버십 구독 시 Shop3 이용약관이 적용되는 것에
                        동의하게 됩니다. *연간 구독료의 경우 월간 멤버십 12개월 구독료 대비 할인된 가격입니다.
                    </Typography>
                </Box>

                {/* Error Display */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: isMobile ? 2 : 3,
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            color: '#dc2626',
                            '& .MuiAlert-icon': {
                                color: '#dc2626',
                            },
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Content Based on Step */}
                {activeStep === 0 && !cardRegistered && !loading && (
                    <Box>
                        <CardRegisterButton
                            onSuccess={handleCardRegistrationSuccess}
                            onError={handleCardRegistrationError}
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={!isCodeVerified}
                            amt={typeof planPrice === 'number' ? planPrice : 1000}
                            planId={planId}
                            planTitle={planTitle}
                            existingCardBid={existingCardBid || undefined}
                            subscriptionPlanId={fittingSubscriptionPlanId}
                            businessCode={businessCode}
                        />
                    </Box>
                )}

                {/* 완료 버튼만 표시 (구독 완료 시) */}
                {subscriptionComplete && (
                    <Box sx={{ mt: isMobile ? 2 : 3 }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleComplete}
                            sx={{
                                backgroundColor: '#FF4B6B',
                                color: '#ffffff',
                                '&:hover': { backgroundColor: '#E03E5A' },
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                                borderRadius: '5px',
                            }}
                            fullWidth
                        >
                            완료
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default SubscriptionCheckoutDialog;
